import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePWAInstall } from '@/contexts/PWAInstallContext';
import { Home, BookOpen, Newspaper, BookCheck, Menu, X, ShoppingBag, Heart, User, LogOut, TrendingUp, Award, BarChart3, MessageCircle, ClipboardList, Bot, Clock, GraduationCap, HelpCircle, Download, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KELAS_CONFIG = {
  D: { label: "I'dad" }, C: { label: 'Uula' }, B: { label: 'Wustha' },
  A: { label: 'Ulya' }, M: { label: 'Ali/Mahir' },
};

const navItems = {
  santri: [
    { icon: Home, label: 'Beranda', path: '/santri' },
    { icon: ClipboardList, label: 'Riwayat Setoran', path: '/santri/setoran' },
    { icon: Award, label: "Tasmi'", path: '/santri/tasmi' },
    { icon: TrendingUp, label: 'Progres', path: '/santri/progress' },
    { icon: MessageCircle, label: 'Chat', path: '/chat' },
  ],
  guru: [
    { icon: Home,       label: 'Beranda',  path: '/guru' },
    { icon: BookCheck,  label: 'Setoran',  path: '/guru/setoran' },
    { icon: Award,      label: "Tasmi'",   path: '/guru/tasmi' },
    { icon: History,    label: 'Riwayat',  path: '/guru/riwayat' },
    { icon: MessageCircle, label: 'Chat',  path: '/chat' },
  ],
  default: [
    { icon: Home, label: 'Beranda', path: '/' },
    { icon: BookOpen, label: 'Al-Quran', path: '/quran' },
    { icon: Clock, label: 'Sholat', path: '/sholat' },
    { icon: Newspaper, label: 'Berita', path: '/berita' },
  ],
};

const DASHBOARD_PATH = { admin: '/admin', guru: '/guru', santri: '/santri' };

const menuFeatures = [
  { icon: BookOpen, label: 'Al-Quran', path: '/quran', color: 'emerald' },
  { icon: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  ), label: 'Tasbih Digital', path: '/tasbih', color: 'amber' },
  { icon: Newspaper, label: 'BQ News', path: '/berita', color: 'blue' },
  { icon: ShoppingBag, label: 'Toko', path: '/toko', color: 'orange' },
  { icon: Heart, label: 'Donasi', path: '/donasi', color: 'red' },
  { icon: BarChart3, label: 'Rekap Absensi', path: '/guru/rekap', color: 'teal' },
  { icon: GraduationCap, label: 'Ujian Tahfidz', path: '/guru/ujian-tahfidz', color: 'purple' },
  { icon: HelpCircle, label: 'Pedoman', path: '/pedoman', color: 'blue' },
];

export default function BottomNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [santriProfile, setSantriProfile] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const { showInstall, handleInstall } = usePWAInstall();

  useEffect(() => {
    if (user?.role === 'santri' && user?.santri_id) {
      axios.get(`${API}/santri/${user.santri_id}`)
        .then(r => setSantriProfile(r.data))
        .catch(() => {});
    }
  }, [user]);

  // Poll unread chat count every 30s for guru/santri
  useEffect(() => {
    if (!user || !['guru', 'santri'].includes(user.role)) return;
    const fetchUnread = () => {
      axios.get(`${API}/chat/unread-count`, { withCredentials: true })
        .then(r => setUnreadTotal(r.data?.count || 0)).catch(() => {});
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const items = navItems[user?.role] || navItems.default;
  const handleNavigate = (path) => { navigate(path); setShowMenu(false); };
  const handleLogout = async () => { await logout(); navigate('/welcome'); setShowMenu(false); };

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-emerald-100 md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -4px 20px rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-md mx-auto flex items-center justify-around py-2 px-2">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const isChatTab = item.path === '/chat';
            return (
              <button
                key={item.path + item.label}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-200 min-w-0"
              >
                <div className={`relative p-1.5 rounded-xl transition-all duration-200 ${
                  isActive ? 'bg-emerald-50 scale-110' : 'hover:bg-emerald-50/50'
                }`}>
                  <Icon size={22} className={`transition-colors duration-200 ${isActive ? 'text-emerald-600' : 'text-emerald-400'}`} />
                  {isActive && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-amber-400 rounded-full" />}
                  {isChatTab && unreadTotal > 0 && (
                    <span data-testid="chat-unread-badge"
                      className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                      {unreadTotal > 9 ? '9+' : unreadTotal}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium transition-colors duration-200 ${isActive ? 'text-emerald-600' : 'text-emerald-400'}`}>{item.label}</span>
              </button>
            );
          })}

          {/* Hamburger Menu Button */}
          <button
            data-testid="nav-menu-hamburger"
            onClick={() => setShowMenu(true)}
            className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-200"
          >
            <div className={`relative p-1.5 rounded-xl transition-all duration-200 ${showMenu ? 'bg-emerald-50 scale-110' : 'hover:bg-emerald-50/50'}`}>
              <Menu size={22} className={showMenu ? 'text-emerald-600' : 'text-emerald-400'} />
            </div>
            <span className={`text-[10px] font-medium ${showMenu ? 'text-emerald-600' : 'text-emerald-400'}`}>Menu</span>
          </button>
        </div>
      </nav>

      {/* Full-Feature Menu Modal */}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[60] flex items-end"
            onClick={e => e.target === e.currentTarget && setShowMenu(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="w-full bg-white rounded-t-3xl"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-200 rounded-full" />
              </div>

              {user?.role === 'santri' ? (
                /* ══ SANTRI-SPECIFIC PANEL ══ */
                <>
                  {/* Santri header — sama style dengan Guru */}
                  <div className="flex items-center justify-between px-5 pt-3 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full ring-2 ring-amber-400 ring-offset-1 overflow-hidden bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        {santriProfile?.foto
                          ? <img src={santriProfile.foto} alt={user.name} className="w-full h-full object-cover" />
                          : <span className="text-emerald-700 font-bold text-sm">{user?.name?.charAt(0)}</span>}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate">{user?.name}</p>
                        {santriProfile?.kelas && (
                          <span className="text-[10px] text-emerald-600 font-semibold">
                            Kelas {santriProfile.kelas} · {KELAS_CONFIG[santriProfile.kelas]?.label}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => setShowMenu(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                      <X size={18} className="text-gray-400" />
                    </button>
                  </div>

                  {/* Menu grid — sama layout dengan Guru */}
                  <div className="grid grid-cols-3 gap-3 px-5 pb-4">
                    {[
                      { icon: User,          label: 'Profil Saya',    path: '/santri/profil',        color: 'emerald' },
                      { icon: MessageCircle, label: 'Chat',           path: '/chat',                  color: 'emerald' },
                      { icon: Award,         label: "Tasmi'",         path: '/santri/tasmi',          color: 'teal'    },
                      { icon: GraduationCap, label: 'Ujian Tahfidz',  path: '/santri/ujian-tahfidz',  color: 'purple'  },
                      { icon: BookOpen,      label: 'Al-Quran',       path: '/quran',                 color: 'emerald' },
                      { icon: () => (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                            <circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                          </svg>
                        ), label: 'Tasbih',           path: '/tasbih',                color: 'amber'   },
                      { icon: Newspaper,     label: 'BQ News',        path: '/berita',                color: 'blue'    },
                      { icon: ShoppingBag,   label: 'Toko',           path: '/toko',                  color: 'orange'  },
                      { icon: Heart,         label: 'Donasi',         path: '/donasi',                color: 'red'     },
                      { icon: HelpCircle,    label: 'Pedoman',        path: '/pedoman',               color: 'blue'    },
                    ].map(({ icon: Icon, label, path, color }) => (
                      <button
                        key={label}
                        data-testid={`menu-santri-${label.toLowerCase().replace(/ /g, '-')}`}
                        onClick={() => handleNavigate(path)}
                        className="flex flex-col items-center gap-2 bg-gray-50 hover:bg-emerald-50 rounded-2xl p-4 transition-all active:scale-95"
                      >
                        <div className={`w-11 h-11 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                          {typeof Icon === 'function' && Icon.length === 0
                            ? <Icon />
                            : <Icon size={20} className={`text-${color}-600`} />}
                        </div>
                        <span className="text-xs font-semibold text-gray-700 text-center leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="mx-5 border-t border-gray-100 mb-3" />
                  <div className="px-5 pb-6 space-y-2">
                    {showInstall && (
                      <button
                        data-testid="menu-pasang-aplikasi-btn"
                        onClick={() => { handleInstall(); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3.5 bg-emerald-50 hover:bg-emerald-100 rounded-2xl transition-all"
                      >
                        <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center">
                          <Download size={18} className="text-emerald-700" />
                        </div>
                        <p className="text-sm font-semibold text-emerald-800">Pasang Aplikasi</p>
                      </button>
                    )}
                    <button
                      data-testid="menu-keluar-btn"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-red-50 hover:bg-red-100 rounded-2xl transition-all"
                    >
                      <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                        <LogOut size={18} className="text-red-500" />
                      </div>
                      <p className="text-sm font-semibold text-red-600">Keluar</p>
                    </button>
                  </div>
                </>
              ) : (
                /* ══ DEFAULT PANEL (Guru/Admin) ══ */
                <>
                  <div className="flex items-center justify-between px-5 pt-3 pb-4">
                    <h3 className="font-playfair text-lg font-bold text-emerald-950">Semua Fitur</h3>
                    <button onClick={() => setShowMenu(false)}
                      className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                      <X size={18} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 px-5 pb-4">
                    {menuFeatures.map(({ icon: Icon, label, path, color }) => (
                      <button key={label} data-testid={`menu-feature-${label.toLowerCase().replace(/ /g, '-')}`}
                        onClick={() => handleNavigate(path)}
                        className="flex flex-col items-center gap-2 bg-gray-50 hover:bg-emerald-50 rounded-2xl p-4 transition-all active:scale-95">
                        <div className={`w-11 h-11 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                          {typeof Icon === 'function' && Icon.length === 0
                            ? <Icon />
                            : <Icon size={20} className={`text-${color}-600`} />}
                        </div>
                        <span className="text-xs font-semibold text-gray-700 text-center leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mx-5 border-t border-gray-100 mb-3" />
                  <div className="px-5 pb-6 space-y-2">
                    <button data-testid="menu-profil-btn"
                      onClick={() => handleNavigate(user?.role === 'guru' ? '/guru/profil' : DASHBOARD_PATH[user?.role] || '/')}
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-emerald-50 hover:bg-emerald-100 rounded-2xl transition-all">
                      <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center">
                        <User size={18} className="text-emerald-700" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-semibold text-emerald-900">{user?.name || 'Profil'}</p>
                        <p className="text-xs text-emerald-600 capitalize">{user?.role}</p>
                      </div>
                    </button>
                    {showInstall && (
                      <button
                        data-testid="menu-pasang-aplikasi-btn"
                        onClick={() => { handleInstall(); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3.5 bg-emerald-50 hover:bg-emerald-100 rounded-2xl transition-all"
                      >
                        <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center">
                          <Download size={18} className="text-emerald-700" />
                        </div>
                        <p className="text-sm font-semibold text-emerald-800">Pasang Aplikasi</p>
                      </button>
                    )}
                    <button data-testid="menu-keluar-btn" onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-red-50 hover:bg-red-100 rounded-2xl transition-all">
                      <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                        <LogOut size={18} className="text-red-500" />
                      </div>
                      <p className="text-sm font-semibold text-red-600">Keluar</p>
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
